import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getAPI } from '../utils/config';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

/**
 * PurchaseSuccess component
 * Handles Stripe checkout success redirect
 * Polls for payment status and updates user credits
 */
const PurchaseSuccess = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, error, expired
  const [pollAttempts, setPollAttempts] = useState(0);
  const [isPolling, setIsPolling] = useState(false);
  const MAX_POLL_ATTEMPTS = 10;
  const POLL_INTERVAL = 2000; // 2 seconds

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      // No session_id in URL, redirect to home
      navigate('/');
      return;
    }

    if (!user) {
      // User not logged in, wait for auth to load
      return;
    }

    // Start polling for payment status only once
    if (!isPolling) {
      setIsPolling(true);
      pollPaymentStatus(sessionId);
    }
  }, [searchParams, user, navigate, isPolling]);

  const pollPaymentStatus = async (sessionId) => {
    try {
      setStatus('checking');
      
      const response = await axios.get(`${getAPI()}/credits/purchase/stripe-status/${sessionId}`);
      const data = response.data;
      
      if (data.payment_status === 'paid') {
        // Payment successful!
        setStatus('success');
        
        // Refresh user data to get updated credits
        try {
          const userResponse = await axios.get(`${getAPI()}/auth/me?user_id=${user.id}`);
          const freshUserData = userResponse.data;
          
          setUser(freshUserData);
          localStorage.setItem('user', JSON.stringify(freshUserData));
          
          // Redirect with credits animation
          setTimeout(() => {
            navigate(`/?purchase_success=true&credits=${data.credits || 0}`);
          }, 1000);
        } catch (err) {
          console.error('Error refreshing user data:', err);
          toast.success('Payment successful! Your credits have been added.', {
            duration: 5000
          });
          
          setTimeout(() => {
            navigate('/');
          }, 2000);
        }
        
      } else if (data.status === 'expired') {
        // Payment session expired
        setStatus('expired');
        toast.error(t('purchase.errors.session_expired'));
        
        setTimeout(() => {
          navigate('/');
        }, 3000);
        
      } else if (pollAttempts < MAX_POLL_ATTEMPTS) {
        // Still processing, continue polling
        setPollAttempts(prev => prev + 1);
        setTimeout(() => pollPaymentStatus(sessionId), POLL_INTERVAL);
        
      } else {
        // Max attempts reached
        setStatus('error');
        toast.error(t('purchase.errors.verify_failed'));
        
        setTimeout(() => {
          navigate('/');
        }, 5000);
      }
      
    } catch (error) {
      console.error('Error checking payment status:', error);
      
      if (pollAttempts < MAX_POLL_ATTEMPTS) {
        // Retry on error
        setPollAttempts(prev => prev + 1);
        setTimeout(() => pollPaymentStatus(sessionId), POLL_INTERVAL);
      } else {
        setStatus('error');
        toast.error(t('purchase.errors.error_verifying'));
        
        setTimeout(() => {
          navigate('/');
        }, 5000);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl p-8 sm:p-12 max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <div className="mb-6">
              <div className="inline-block">
                <svg 
                  className="animate-spin h-16 w-16 text-blue-500" 
                  viewBox="0 0 24 24"
                >
                  <circle 
                    className="opacity-25" 
                    cx="12" 
                    cy="12" 
                    r="10" 
                    stroke="currentColor" 
                    strokeWidth="4" 
                    fill="none"
                  />
                  <path 
                    className="opacity-75" 
                    fill="currentColor" 
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4">
              Processing Your Payment
            </h2>
            <p className="text-gray-600">
              Please wait while we confirm your purchase...
            </p>
            <p className="text-sm text-gray-400 mt-4">
              This usually takes just a few seconds
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="mb-6">
              <div className="inline-block bg-green-100 rounded-full p-4">
                <svg 
                  className="w-16 h-16 text-green-500" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M5 13l4 4L19 7" 
                  />
                </svg>
              </div>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4">
              Payment Successful! 🎉
            </h2>
            <p className="text-gray-600 mb-6">
              Your credits have been added to your account.
            </p>
            <p className="text-sm text-gray-500">
              Redirecting you back to the app...
            </p>
          </>
        )}

        {status === 'expired' && (
          <>
            <div className="mb-6">
              <div className="inline-block bg-yellow-100 rounded-full p-4">
                <svg 
                  className="w-16 h-16 text-yellow-500" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                  />
                </svg>
              </div>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4">
              Session Expired
            </h2>
            <p className="text-gray-600 mb-6">
              Your payment session has expired. Please try again.
            </p>
            <p className="text-sm text-gray-500">
              Redirecting you back to the app...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="mb-6">
              <div className="inline-block bg-red-100 rounded-full p-4">
                <svg 
                  className="w-16 h-16 text-red-500" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M6 18L18 6M6 6l12 12" 
                  />
                </svg>
              </div>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4">
              Unable to Verify Payment
            </h2>
            <p className="text-gray-600 mb-6">
              {t('purchase.verify_failed_desc')}
            </p>
            <p className="text-sm text-gray-500">
              Redirecting you back to the app...
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default PurchaseSuccess;
